<script setup lang="ts">
  import { CartLayout, CartWithSummaryLayout } from '@storefront/components';
  import { type FormContextInterface, setModelWithContext, useHttpClient, useRoute } from '@othercommerce/foundation';
  import { useVerifiedCart, addPaymentInfoEvent, selectContentEvent, useGoogleTagManager } from '@storefront/storefront';
  import type { BillingOption, SharedOrder } from '@storefront/resources';

  defineOptions({
    layout: [CartLayout, CartWithSummaryLayout],
    inheritAttrs: false,
  });

  interface Props {
    options: BillingOption[];
  }

  defineProps<Props>();

  const cart = useVerifiedCart();
  const http = useHttpClient();
  const gtm = useGoogleTagManager();
  const route = useRoute();

  function isSelected(option: BillingOption, data: SharedOrder) {
    return (
      data.billingMethodId === option.billingMethodId &&
      data.billingMethodOptionId === option.id
    );
  }

  function onChoose(option: BillingOption, ctx: FormContextInterface) {
    setModelWithContext('billingMethodId', ctx, option.billingMethodId);
    setModelWithContext('billingMethodOptionId', ctx, option.id);
    gtm.push(selectContentEvent('billingMethod', option.name));
  }

  function onSubmit(data: any) {
    // Send only required data, otherwhise request can be to big
    const { items, ...payload } = data;

    return http
      .patch(route('web.cart.billing.option.update', payload))
      .then(() => gtm.push(addPaymentInfoEvent(cart.value)));
  }

</script>
<template>
  <FormController :data="cart" v-slot="{ data, processing, ctx }" @submit="onSubmit">
    <div class="billing-method-form">
      <template v-for="option in options">
        <SfListCard class="billing-method-form__option" interactive @click="onChoose(option, ctx)" @keydown.space="onChoose(option, ctx)" :selected="isSelected(option, data)" tabindex="0" role="button">
          <header class="billing-method-form__header">
            <img :src="option.icon" :alt="option.name" class="billing-method-form__image" />
            <div class="billing-method-form__name-container">
              <h2 class="billing-method-form__name">{{ option.name }}</h2>
            </div>
          </header>
        </SfListCard>
      </template>
    </div>
    <UiButtonGroup>
      <SfButton primary :loading="processing" type="submit">
        {{ $t('SHOP.ACTION.CONTINUE') }}
      </SfButton>
    </UiButtonGroup>
  </FormController>

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .billing-method-form {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: var(--form-gap-y) var(--form-gap-x);

    &__option {
      display: flex;
    }

    &__header {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      height: 100%;
    }

    &__image {
      flex: 0 0 auto;
      width: 100%;
      object-position: center;
      object-fit: scale-down;
      aspect-ratio: 4/3;
    }

    &__name-container {
      flex: 1 1 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &__name {
      font-weight: var(--font-weight-regular);
      font-size: 0.75rem;
      text-align: center;
    }

    @include media.query(md) {
      grid-template-columns: repeat(4, 1fr);
      gap: 0.5rem;
    }

    @include media.query(sm) {
      grid-template-columns: repeat(3, 1fr);
    }
  }
</style>
